import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { payrollApi, bizPayrollApi, type SavePayrollConfigInput } from "../lib/payrollApi";

export const PAYROLL_KEYS = {
  all: ["payroll"] as const,
  config: () => [...PAYROLL_KEYS.all, "config"] as const,
  runStatus: (period: string) => [...PAYROLL_KEYS.all, "run", period] as const,
  // Biz-scoped key namespaces
  biz: (businessId: string) => ["bizPayroll", businessId] as const,
  bizConfig: (businessId: string) => ["bizPayroll", businessId, "config"] as const,
  bizRuns: (businessId: string) => ["bizPayroll", businessId, "runs"] as const,
  bizRunStatus: (businessId: string, period: string) =>
    ["bizPayroll", businessId, "run", period] as const,
};

// ── Super-admin hooks (use /admin/payroll/... routes) ─────────────────────────

export function usePayrollRuns(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...PAYROLL_KEYS.all, "runs"] as const,
    queryFn: () => payrollApi.listRuns().then((r) => r.runs),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}

export function usePayrollConfig(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: PAYROLL_KEYS.config(),
    queryFn: () => payrollApi.getConfig().then((r) => r.config),
    enabled: options?.enabled ?? true,
  });
}

export function usePayrollRunStatus(period: string) {
  return useQuery({
    queryKey: PAYROLL_KEYS.runStatus(period),
    queryFn: () => payrollApi.getRunStatus(period).then((r) => r.run),
    enabled: !!period,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}

export function useSavePayrollConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SavePayrollConfigInput) => payrollApi.saveConfig(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: PAYROLL_KEYS.config() }),
  });
}

export function useTriggerPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (period: string) => payrollApi.process(period),
    onSuccess: (_, period) => {
      qc.invalidateQueries({ queryKey: [...PAYROLL_KEYS.all, "runs"] });
      qc.invalidateQueries({ queryKey: PAYROLL_KEYS.runStatus(period) });
    },
  });
}

export function useApprovePayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (period: string) => payrollApi.approve(period),
    onSuccess: () => qc.invalidateQueries({ queryKey: PAYROLL_KEYS.all }),
  });
}

// ── Business-scoped hooks (use /biz/{businessId}/payroll/... routes) ──────────

export function useBizPayrollRuns(businessId: string) {
  return useQuery({
    queryKey: PAYROLL_KEYS.bizRuns(businessId),
    queryFn: () => bizPayrollApi.listRuns(businessId).then((r) => r.runs),
    enabled: !!businessId,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}

export function useBizPayrollConfig(businessId: string) {
  return useQuery({
    queryKey: PAYROLL_KEYS.bizConfig(businessId),
    queryFn: () => bizPayrollApi.getConfig(businessId).then((r) => r.config),
    enabled: !!businessId,
  });
}

export function useBizPayrollRunStatus(businessId: string, period: string) {
  return useQuery({
    queryKey: PAYROLL_KEYS.bizRunStatus(businessId, period),
    queryFn: () => bizPayrollApi.getRunStatus(businessId, period).then((r) => r.run),
    enabled: !!businessId && !!period,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}

export function useSaveBizPayrollConfig(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SavePayrollConfigInput) =>
      bizPayrollApi.saveConfig(businessId, input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: PAYROLL_KEYS.bizConfig(businessId) }),
  });
}

export function useTriggerBizPayroll(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (period: string) => bizPayrollApi.process(businessId, period),
    onSuccess: (_, period) => {
      qc.invalidateQueries({ queryKey: PAYROLL_KEYS.bizRuns(businessId) });
      qc.invalidateQueries({ queryKey: PAYROLL_KEYS.bizRunStatus(businessId, period) });
    },
  });
}

export function useApproveBizPayroll(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (period: string) => bizPayrollApi.approve(businessId, period),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: PAYROLL_KEYS.biz(businessId) }),
  });
}
